import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Badge, Button, Col, Container, Row } from "react-bootstrap";
import { auth } from "../../config/firebaseConfig";
import ColM from "../../assets/components/column/ColM";
import Loader from "../../assets/components/loader/Loader";
import messageBox from "../../assets/components/message/MessageBox";
import ConfirmOrderModal from "../../components/modals/ConfirmOrderModal";
import SendQuoteModal from "../../components/modals/SendQuoteModal";
import { baseUrl } from "../../config/serverConfig";
import useAxios from "../../hooks/axios/useAxios";
import { DateShortTime, fromSeconds } from "../../hooks/helpers/timeUtils";
import { DefaultResponseType } from "../../types/axiosTypes";
import { Order } from "../../types/orderTypes";
import { PricesProvider, usePrices } from "../../context/prices/PricesContext";
import RegelsTabel, { regelsUitSnapshot } from "../../components/createOrder/RegelsTabel";

// ─── helpers ─────────────────────────────────────────────────────────────────

const euro = (amount: number) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(
    amount
  );

/** Hele centen naar een bedrag; de partnervelden zijn integers in centen. */
const euroCents = (cents: number) => euro(cents / 100);

// Snapshot van custom vaste kosten op de order: JSON [{name, value}, ...].
const parseFixedCosts = (
  json: string | null | undefined
): { name: string; value: number }[] => {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (fc): fc is { name: string; value: number } =>
        fc && typeof fc.name === "string" && typeof fc.value === "number"
    );
  } catch {
    return [];
  }
};

const LabelValue = ({
  label,
  value,
  xs = 6,
  md = 3,
}: {
  label: string;
  value: string | number | null | undefined;
  xs?: number;
  md?: number;
}) => (
  <Col xs={xs} md={md} className="mb-3">
    <div
      style={{
        fontSize: "0.72rem",
        color: "#6c757d",
        marginBottom: 2,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </div>
    <div style={{ fontWeight: 500 }}>{value ?? "—"}</div>
  </Col>
);

const SectionTitle = ({ children }: { children: string }) => (
  <Col xs={12}>
    <h6
      style={{
        fontWeight: 600,
        fontSize: "0.8rem",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "#6c757d",
        borderBottom: "1px solid #e9ecef",
        paddingBottom: 6,
        marginBottom: 16,
        marginTop: 4,
      }}
    >
      {children}
    </h6>
  </Col>
);

const Divider = () => (
  <Col xs={12}>
    <hr style={{ margin: "4px 0 16px" }} />
  </Col>
);

// ─── page ─────────────────────────────────────────────────────────────────────

const OrderPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const stateOrder = (location.state as { order: Order } | null)?.order ?? null;
  const [order, setOrder] = useState<Order | null>(stateOrder);
  const [loading, setLoading] = useState(!stateOrder);
  const [refresh, setRefresh] = useState(false);
  const isFirstRender = useRef(true);
  const { getMaterialName } = usePrices();

  const { fetchData } = useAxios({ method: "get" });
  // Welk document er nu gedownload wordt ("pdf", "orderbevestiging", "paklijst").
  const [downloading, setDownloading] = useState<string | null>(null);

  /** Haalt een PDF van de order op (offerte, orderbevestiging of paklijst). */
  const download = async (pad: string, bestandsnaam: string, fout: string) => {
    if (!order) return;
    setDownloading(pad);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${baseUrl}/order/${order.id}/${pad}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // De server legt uit waarom (bijvoorbeeld: een order zonder artikelregels).
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? fout);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = bestandsnaam;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      messageBox({ message: e instanceof Error ? e.message : fout, type: "error" });
    } finally {
      setDownloading(null);
    }
  };

  const fetchOrder = async ({ stil = false } = {}) => {
    const response: DefaultResponseType<Order> = await fetchData(
      `${baseUrl}/order/${id}`
    );
    if (!response.error) {
      setOrder(response.data);
    } else if (!stil) {
      messageBox({ message: "Order niet gevonden", type: "error" });
      navigate("/orders");
    }
  };

  // Altijd ophalen, ook vanuit de lijst: alleen GET /order/:id zegt welke
  // documenten deze gebruiker bij deze order kan downloaden. Met route state
  // staat de order er meteen; een mislukte verversing laat die gewoon staan.
  useEffect(() => {
    (async () => {
      await fetchOrder({ stil: !!stateOrder });
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  // Re-fetch after promote — skip the very first render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchOrder();
    // eslint-disable-next-line
  }, [refresh]);

  if (loading) return <Loader pageTitle="Order laden" />;
  if (!order) return null;

  const shortId = order.id.split("-")[0].toUpperCase();
  const createdDate = order.created_at
    ? DateShortTime(fromSeconds(order.created_at))
    : "—";

  return (
    <Container fluid>
      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <Row>
        <ColM xs={12}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <div className="d-flex align-items-center gap-3 mb-1">
                <h4 style={{ fontWeight: 200, marginBottom: 0 }}>
                  Order #{shortId}
                </h4>
                <Badge
                  bg={order.input_type === "ORDER" ? "success" : "warning"}
                  text={order.input_type === "ORDER" ? undefined : "dark"}
                  style={{ fontSize: "0.8rem", padding: "5px 10px" }}
                >
                  {order.input_type}
                </Badge>
                {order.partner && (
                  <Badge
                    bg={order.partner.afwijking ? "danger" : "info"}
                    style={{ fontSize: "0.8rem", padding: "5px 10px" }}
                    title={
                      order.partner.afwijking
                        ? `De partner rekende ${euroCents(
                            order.partner.partnerTotaalCents
                          )}, wij ${euroCents(
                            order.partner.serverTotaalCents
                          )}. Het bedrag op deze order is het onze.`
                        : `Via de partner-API binnengekomen, bedrag komt op de cent overeen. Referentie ${order.partner.referentie}.`
                    }
                  >
                    {order.partner.afwijking
                      ? `Partner-API — AFWIJKEND BEDRAG (${euroCents(
                          order.partner.verschilCents
                        )})`
                      : "Partner-API"}
                  </Badge>
                )}
              </div>
              {order.partner?.afwijking && (
                <Alert variant="danger" className="py-2 px-3 mb-2">
                  <strong>Let op:</strong> deze order kwam via de partner-API
                  binnen met een ander bedrag. De partner rekende{" "}
                  {euroCents(order.partner.partnerTotaalCents)}, onze calculatie komt
                  uit op {euroCents(order.partner.serverTotaalCents)} — een verschil
                  van {euroCents(order.partner.verschilCents)}. Op deze order staat
                  ons bedrag. Referentie van de partner:{" "}
                  <code>{order.partner.referentie}</code>.
                </Alert>
              )}
              <small className="text-muted">Aangemaakt op {createdDate}</small>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate("/orders")}
              >
                ← Terug naar overzicht
              </Button>
              {order.input_type === "OFFERTE" && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() =>
                    download(
                      "pdf",
                      `Offerte-${shortId}-${order.last_name}.pdf`,
                      "PDF genereren mislukt"
                    )
                  }
                  disabled={!!downloading}
                >
                  {downloading === "pdf" ? "Genereren..." : "Download Offerte PDF"}
                </Button>
              )}
              {order.input_type === "ORDER" && order.documenten?.orderbevestiging && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() =>
                    download(
                      "orderbevestiging",
                      `Orderbevestiging-${shortId}-${order.last_name}.pdf`,
                      "Orderbevestiging genereren mislukt"
                    )
                  }
                  disabled={!!downloading}
                >
                  {downloading === "orderbevestiging" ? "Genereren..." : "Download orderbevestiging"}
                </Button>
              )}
              {order.input_type === "ORDER" && order.documenten?.paklijst && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() =>
                    download(
                      "paklijst",
                      `Paklijst-${shortId}-${order.last_name}.pdf`,
                      "Paklijst genereren mislukt"
                    )
                  }
                  disabled={!!downloading}
                >
                  {downloading === "paklijst" ? "Genereren..." : "Download paklijst"}
                </Button>
              )}
              {order.input_type === "OFFERTE" &&
                order.emailSent &&
                order.emailSentAt && (
                  <Badge
                    bg="success"
                    style={{
                      fontSize: "0.78rem",
                      padding: "7px 11px",
                      fontWeight: 500,
                    }}
                  >
                    Email verzonden op{" "}
                    {DateShortTime(fromSeconds(order.emailSentAt))}
                  </Badge>
                )}
              {order.input_type === "OFFERTE" && !!order.email && (
                <SendQuoteModal
                  order={order}
                  refresh={refresh}
                  setRefresh={setRefresh}
                  resend={!!order.emailSent}
                />
              )}
              {order.input_type === "OFFERTE" && (
                <ConfirmOrderModal
                  order={order}
                  refresh={refresh}
                  setRefresh={setRefresh}
                />
              )}
            </div>
          </div>
        </ColM>
      </Row>

      {order.documenten?.melding && (
        <Row>
          <ColM xs={12}>
            <Alert variant="secondary" className="mb-0 py-2" style={{ fontSize: "0.85rem" }}>
              {order.documenten.melding}
            </Alert>
          </ColM>
        </Row>
      )}

      {/* ── KLANT + VERKOPER ────────────────────────────────────────────── */}
      <Row>
        <ColM xs={12} md={6}>
          <Row>
            <SectionTitle>Klant gegevens</SectionTitle>
            <LabelValue
              label="Naam"
              value={`${order.first_name} ${order.last_name}`}
              xs={12}
              md={6}
            />
            <LabelValue label="E-mail" value={order.email} xs={12} md={6} />
            <LabelValue label="Telefoon" value={order.phone} xs={12} md={6} />
            <LabelValue
              label="Adres"
              value={`${order.street} ${order.house_number}`}
              xs={12}
              md={6}
            />
            <LabelValue label="Postcode" value={order.zipcode} xs={6} md={3} />
            <LabelValue label="Stad" value={order.city} xs={6} md={3} />
          </Row>
        </ColM>

        <ColM xs={12} md={6}>
          <Row>
            <SectionTitle>Verkoper</SectionTitle>
            <LabelValue
              label="Naam"
              value={`${order.user.first_name} ${order.user.last_name}`}
              xs={12}
              md={6}
            />
            <LabelValue label="Bedrijf" value={order.office.name} xs={12} md={6} />
            <LabelValue label="E-mail" value={order.user.email} xs={12} md={6} />
          </Row>
        </ColM>
      </Row>

      {/* ── TRAP CONFIGURATIE ───────────────────────────────────────────── */}
      <Row>
        <ColM xs={12}>
          <Row>
            <SectionTitle>Trap configuratie</SectionTitle>

            <LabelValue label="Type trap" value={order.type} />
            <LabelValue label="Profielkleur" value={order.profileColor} />
            {/* Alleen orders vanaf rekenversie 2 hebben breedte en trapvorm. */}
            {order.breedte && <LabelValue label="Breedte" value={`${order.breedte} cm`} />}
            {order.trapvorm && (
              <LabelValue
                label="Trapvorm"
                value={order.trapvorm.charAt(0).toUpperCase() + order.trapvorm.slice(1)}
              />
            )}

            <Divider />
            <Col xs={12} className="mb-2">
              <strong style={{ fontSize: "0.9rem" }}>Treden</strong>
            </Col>
            <LabelValue label="Materiaal" value={getMaterialName(order.stairMaterial)} />
            <LabelValue label="Kleur" value={order.stairColor} />
            <LabelValue label="Aantal" value={order.stairAmount} />
            <LabelValue label="Prijs" value={euro(order.stairPrice)} />

            {order.risersMaterial && (
              <>
                <Divider />
                <Col xs={12} className="mb-2">
                  <strong style={{ fontSize: "0.9rem" }}>Stootborden</strong>
                </Col>
                <LabelValue label="Materiaal" value={`${getMaterialName(order.risersMaterial)} (zelfde als treden)`} />
                <LabelValue label="Kleur" value={order.risersColor ?? "—"} />
                <LabelValue label="Aantal" value={order.risersAmount ?? "—"} />
                <LabelValue
                  label="Prijs"
                  value={euro(order.risersPrice ?? 0)}
                />
              </>
            )}

            {order.specialRiserId && (
              <>
                <Divider />
                <Col xs={12} className="mb-2">
                  <strong style={{ fontSize: "0.9rem" }}>Stootborden (speciaal)</strong>
                </Col>
                <LabelValue label="Product" value={order.specialRiser?.name ?? "—"} />
                <LabelValue label="Aantal" value={order.specialRiserAmount ?? "—"} />
                <LabelValue
                  label="Prijs"
                  value={euro(order.specialRiserPrice ?? 0)}
                />
              </>
            )}

            <Divider />
            <Col xs={12} className="mb-2">
              <strong style={{ fontSize: "0.9rem" }}>Bovenaansluiting</strong>
            </Col>
            <LabelValue label="Materiaal" value={getMaterialName(order.topMaterial)} />
            <LabelValue label="Kleur" value={order.topColor} />
            <LabelValue label="Aantal" value={order.topAmount} />
            <LabelValue label="Prijs" value={euro(order.topPrice)} />

            {(order.volfineerOpenPrice ?? 0) > 0 && (
              <>
                <Divider />
                <Col xs={12} className="mb-2">
                  <strong style={{ fontSize: "0.9rem" }}>Volfineer open trap</strong>
                </Col>
                <LabelValue
                  label="Materiaal"
                  value={`${getMaterialName(order.stairMaterial)} (zelfde als treden)`}
                />
                <LabelValue label="Kleur" value={order.stairColor ?? "—"} />
                <LabelValue label="Aantal" value={order.stairAmount} />
                <LabelValue label="Prijs" value={euro(order.volfineerOpenPrice ?? 0)} />
              </>
            )}

            <Divider />
            <Col xs={12} className="mb-2">
              <strong style={{ fontSize: "0.9rem" }}>Extras</strong>
            </Col>
            <LabelValue
              label="LED verlichting"
              value={
                order.extrasLed
                  ? order.ledMode === "ELKE_TREDE"
                    ? "Elke trede"
                    : order.ledMode === "OM_EN_OM"
                    ? "Om en om"
                    : order.ledMode === "ANDERS"
                    ? `Anders: ${order.ledCustomText || ""}`
                    : "Ja"
                  : "Nee"
              }
            />
            <LabelValue
              label="Plateau"
              value={order.extrasPlateau ? "Ja" : "Nee"}
            />
            {order.extrasPlateau && (
              <LabelValue
                label="Plateau materiaal"
                value={getMaterialName(order.plateauMaterial)}
              />
            )}
            {order.extrasPlateau && order.plateauSize && (
              <LabelValue
                label="Plateau maat"
                value={`${order.plateauSize} cm`}
              />
            )}
            <LabelValue
              label="Volfineer"
              value={order.extrasVolfineer ? "Ja" : "Nee"}
            />
            <LabelValue
              label="Montage"
              value={order.assembly ? "Ja" : "Nee"}
            />
          </Row>
        </ColM>
      </Row>

      {/* ── PRIJSOVERZICHT ──────────────────────────────────────────────── */}
      <Row>
        <ColM xs={12}>
          <Row>
            <SectionTitle>Prijsoverzicht</SectionTitle>
            {regelsUitSnapshot(order).length > 0 && (
              <Col xs={12} className="mb-3">
                <RegelsTabel regels={regelsUitSnapshot(order)} />
              </Col>
            )}
            <LabelValue label="Treden" value={euro(order.stairPrice)} />
            {(order.risersPrice ?? 0) > 0 && (
              <LabelValue
                label="Stootborden"
                value={euro(order.risersPrice ?? 0)}
              />
            )}
            {(order.specialRiserPrice ?? 0) > 0 && (
              <LabelValue
                label="Stootborden (speciaal)"
                value={euro(order.specialRiserPrice ?? 0)}
              />
            )}
            {order.topPrice > 0 && (
              <LabelValue label="Bovenaansluiting" value={euro(order.topPrice)} />
            )}
            {order.assembly && order.assemblyPrice > 0 && (
              <LabelValue label="Montage" value={euro(order.assemblyPrice)} />
            )}
            {order.extrasLed && order.ledPricing > 0 && (
              <LabelValue
                label="LED verlichting"
                value={euro(order.ledPricing)}
              />
            )}
            {(order.ledAssemblyPrice ?? 0) > 0 && (
              <LabelValue
                label="Montage LED"
                value={euro(order.ledAssemblyPrice ?? 0)}
              />
            )}
            {order.letherPricing > 0 && (
              <LabelValue label="Leder" value={euro(order.letherPricing)} />
            )}
            {order.extrasPlateau && order.plateauPrice > 0 && (
              <LabelValue label="Plateau" value={euro(order.plateauPrice)} />
            )}
            {order.extrasVolfineer && (order.volfineerPrice ?? 0) > 0 && (
              <LabelValue
                label="Volfineer"
                value={euro(order.volfineerPrice ?? 0)}
              />
            )}
            {(order.volfineerOpenPrice ?? 0) > 0 && (
              <LabelValue
                label={`Volfineer open trap (${order.stairAmount} treden, ${getMaterialName(order.stairMaterial)})`}
                value={euro(order.volfineerOpenPrice ?? 0)}
              />
            )}
            {parseFixedCosts(order.fixedCostsJson).map((fc) => (
              <LabelValue key={fc.name} label={fc.name} value={euro(fc.value)} />
            ))}
            <LabelValue label="Marge" value={`${order.margin}%`} />
          </Row>

          <div
            style={{
              borderTop: "2px solid #e9ecef",
              paddingTop: 16,
              marginTop: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 6,
            }}
          >
            <div style={{ color: "#6c757d", fontSize: "0.9rem" }}>
              Inkoop (ex. btw):{" "}
              <strong style={{ color: "#1a1a2e" }}>
                {euro(order.finalPrice)}
              </strong>
            </div>
            <div style={{ color: "#6c757d", fontSize: "0.9rem" }}>
              Advies verkoop (ex. btw):{" "}
              <strong style={{ color: "#1a1a2e" }}>
                {order.bedragen ? euro(order.bedragen.verkoopExclBtwCents / 100) : "—"}
              </strong>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 12,
                marginTop: 4,
              }}
            >
              <span style={{ color: "#6c757d", fontSize: "0.9rem" }}>
                Advies verkoop (incl. btw)
              </span>
              <span
                style={{ fontSize: "1.6rem", fontWeight: 600, color: "#0d6efd" }}
              >
                {order.bedragen ? euro(order.bedragen.verkoopInclBtwCents / 100) : "—"}
              </span>
            </div>
          </div>
        </ColM>
      </Row>
    </Container>
  );
};

const OrderPageWithPrices = () => (
  <PricesProvider>
    <OrderPage />
  </PricesProvider>
);

export default OrderPageWithPrices;
